/**
 * Clear Insurance Actions
 *
 * Assigner functions for resetting insurance data in context.
 * Export plain functions here; the machine wraps them with `assign()` inside
 * `setup()` so XState's phantom types stay compatible (no `as any` needed).
 */

import { useBoundStoreBase } from "@frontend/lib/stores/globalStore";
import type { InsuranceConnectorContext } from "../types";

/**
 * Clear all insurance data — keeps consumer and user data intact.
 *
 * Also resets `isPendingPricesMode` and `storefrontPaymentMode` so that
 * surfaces gated on machine context (e.g. `InsuranceConnectInline`'s
 * "Browse treatments" CTA, `InsuranceStatusButton`'s pending state) revert
 * to the not-connected presentation after a disconnect / "try another" /
 * "continue without insurance" transition. The companion `setInsurances([])`
 * + `setIsPendingPricesMode(false)` writes to the global store happen in the
 * paired `clearStoreInsurance` / `clearPendingPricesMode` actions.
 */
export const clearInsuranceAssigner =
  (): Partial<InsuranceConnectorContext> => ({
    primaryInsurance: undefined,
    secondaryInsurance: undefined,
    error: undefined,
    verificationAttempts: 0,
    lastVerificationResults: undefined,
    isPendingPricesMode: false,
    storefrontPaymentMode: "CASH",
  });

/**
 * Clear insurance data from global store only
 */
export const clearInsuranceFromStore = () => {
  const { setInsurances, setLastInsuranceCheckResults } =
    useBoundStoreBase.getState();

  setInsurances([]);
  setLastInsuranceCheckResults(null);
};

/**
 * Clear only secondary insurance data
 */
export const clearSecondaryInsuranceAssigner =
  (): Partial<InsuranceConnectorContext> => ({
    secondaryInsurance: undefined,
  });

/**
 * Clear error state
 */
export const clearErrorAssigner = (): Partial<InsuranceConnectorContext> => ({
  error: undefined,
});

/**
 * Reset entire form (go back to initial state)
 */
export const resetFormAssigner = (): Partial<InsuranceConnectorContext> => ({
  firstName: undefined,
  lastName: undefined,
  birthday: undefined,
  consumerState: undefined,
  primaryInsurance: undefined,
  secondaryInsurance: undefined,
  isReturnUser: undefined,
  consumerId: undefined,
  userId: undefined,
  error: undefined,
  isProcessing: false,
  isRetryFlow: false,
});
